import { type Feature, featureCollection, type Point, point } from '@turf/helpers';
import isEqual from 'lodash.isequal';
import type { MapMouseEvent, SymbolLayerSpecification } from 'mapbox-gl';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { Layer, Source } from 'react-map-gl/mapbox';
import logCustomRouteContextualEditMenuViewed from '@alltrails/amplitude/events/logCustomRouteContextualEditMenuViewed';
import logCustomRouteUpdated from '@alltrails/amplitude/events/logCustomRouteUpdated';
import useValueRef from '@alltrails/core/hooks/useValueRef';
import type { MarkerProperties } from '@alltrails/types/CustomRoute';
import useMap from '../../../hooks/useMap';
import { useAppDispatch, useAppSelector } from '../../../redux';
import { setActiveSegmentIndex, setSelectedHandleIndex } from '../../../redux/customRoute';
import { getMarkerPosition } from '../../../utils/customRoutes/overlappingPoints';
import { isLinkedToNextSegment, isLinkedToPreviousSegment } from '../../../utils/customRoutes/segmentLinking';
import { customRouteMarkerDragId, customRouteMarkersId, defaultSymbolLayerLayoutProps, getLayerBeforeId } from '../../../utils/layers';
import endSvg from '../../../utils/svgDefinitions/endSvg';
import handleSvg from '../../../utils/svgDefinitions/handleSvg';
import startEndSvg from '../../../utils/svgDefinitions/startEndSvg';
import startSvg from '../../../utils/svgDefinitions/startSvg';
import { MapLayerInteractionsContext } from '../../MapLayerInteractionsProvider';
import { DrawControlsContext } from '../DrawControls/DrawControlsProvider';
import ContextMenu from './ContextMenu';
import PositionTooltip from './PositionTooltip';
import PulsingMarker from './PulsingMarker';

export const SOURCE_ID = 'custom-route-markers-source';
export const DRAG_SOURCE_ID = 'custom-route-marker-drag-source';
export const MARKER_PULSE_SOURCE_ID = 'marker-pulse';
export const MARKER_PULSE_LAYER_ID = 'marker-pulse-layer';
const IS_START_EXPRESSION = ['==', ['get', 'isStart'], true];
const IS_END_EXPRESSION = ['==', ['get', 'isEnd'], true];
const IS_LINKED_EXPRESSION = ['==', ['get', 'isLinkedToAdjacentSegment'], true];

// Layout is shared between all markers
const layout: SymbolLayerSpecification['layout'] = {
  ...defaultSymbolLayerLayoutProps,
  'symbol-sort-key': ['case', IS_START_EXPRESSION, 0, IS_END_EXPRESSION, 2, IS_LINKED_EXPRESSION, 2, 1],
  'icon-image': [
    'case',
    ['all', IS_START_EXPRESSION, IS_END_EXPRESSION],
    startEndSvg.name,
    IS_LINKED_EXPRESSION,
    startEndSvg.name,
    IS_START_EXPRESSION,
    startSvg.name,
    IS_END_EXPRESSION,
    endSvg.name,
    handleSvg.name
  ],
  'icon-allow-overlap': true
};

const getMarkerId = (segmentIndex: number, markerIndex: number) => {
  return `segment-${segmentIndex}-marker-${markerIndex}`;
};

const Markers = () => {
  const map = useMap();
  const dispatch = useAppDispatch();
  const beforeId = getLayerBeforeId(map, customRouteMarkersId);
  const { isEditing, activeSegmentIndex, selectedHandleIndex, allSegments, isEditingWaypoint } = useAppSelector(state => ({
    isEditing: state.customRoute.isEditing,
    activeSegmentIndex: state.customRoute.activeSegmentIndex,
    selectedHandleIndex: state.customRoute.selectedHandleIndex,
    allSegments: state.customRoute.route.segments,
    isEditingWaypoint: state.customRoute.isEditingWaypoint
  }));

  const selectedPoint = useMemo(() => {
    if (activeSegmentIndex === undefined || selectedHandleIndex === undefined) {
      return undefined;
    }

    const segment = allSegments[activeSegmentIndex];
    if (!segment || selectedHandleIndex > segment.paths.length) {
      return undefined;
    }

    return selectedHandleIndex === segment.paths.length
      ? segment.paths[segment.paths.length - 1].points[segment.paths[segment.paths.length - 1].points.length - 1]
      : segment.paths[selectedHandleIndex].points[0];
  }, [activeSegmentIndex, allSegments, selectedHandleIndex]);

  const selectedHandleIndexRef = useValueRef(selectedHandleIndex);

  const { registerEventHandler, unregisterEventHandler } = useContext(MapLayerInteractionsContext);
  const { pendingHandle, dragMarker, hoveredMarker, isContextMenuOpen, isContextMenuOpenRef, setIsContextMenuOpen, showPositionTooltip } =
    useContext(DrawControlsContext);

  // Identity of the marker being dragged, rather than dragMarker itself, so this is stable across the drag's mousemoves
  const draggedMarkerId = dragMarker?.id;

  const dragData = useMemo(() => {
    if (!dragMarker) {
      return featureCollection<Point, MarkerProperties>([]);
    }

    const points: Feature<Point, MarkerProperties>[] = [];
    const { segmentIndex, endPathIndex, isStart, isEnd, latitude, longitude } = dragMarker;

    if (isStart && isEnd) {
      // Default combined start/end marker selection is the endpoint so if a combined marker is being dragged while another handle is selected we're really dragging the end handle
      // We only drag the start handle when the start handle is selected
      const isDraggingEnd = selectedHandleIndex !== 0;
      const startProperties = { ...dragMarker, id: getMarkerId(segmentIndex, 0), isStart: true, isEnd: false, pathIndex: 0, endPathIndex: undefined };
      const endProperties = {
        ...dragMarker,
        id: getMarkerId(segmentIndex, endPathIndex! + 1),
        isStart: false,
        isEnd: true,
        pathIndex: endPathIndex!,
        endPathIndex: undefined
      };

      // Since the drag marker is excluded from the main source we need to render both the handle being dragged and the remaining handle that would result after splitting the combined marker
      points.push(
        point<MarkerProperties>([longitude, latitude], {
          ...(isDraggingEnd ? endProperties : startProperties),
          latitude,
          longitude
        })
      );

      if (selectedPoint) {
        points.push(
          point<MarkerProperties>([selectedPoint[1], selectedPoint[0]], {
            ...(isDraggingEnd ? startProperties : endProperties),
            latitude: selectedPoint[0],
            longitude: selectedPoint[1]
          })
        );
      }
    } else {
      points.push(point<MarkerProperties>([longitude, latitude], dragMarker));
    }

    return featureCollection<Point, MarkerProperties>(points);
  }, [dragMarker, selectedHandleIndex, selectedPoint]);

  const data = useMemo(() => {
    // If there is no active segment, show markers for all segments
    const segmentsWithIndex =
      activeSegmentIndex === undefined
        ? allSegments.map((segment, index) => ({ segment, index }))
        : allSegments[activeSegmentIndex]
          ? [{ segment: allSegments[activeSegmentIndex], index: activeSegmentIndex }]
          : [];
    const markers = segmentsWithIndex
      .map(({ segment, index: segmentIndex }) => {
        const { paths } = segment;

        if (!paths || paths.length === 0 || paths.some(path => path.points.length === 0)) {
          return [];
        }

        const points = paths.map(path => path.points[0]);
        const lastPath = paths[paths.length - 1];
        const lastPoint = lastPath.points[lastPath.points.length - 1];

        // If the segment has more than one point we need to add the last point to allow drawing the end handle
        if (paths[0].points.length > 1) {
          points.push(lastPoint);
        }

        // If there is a pending handle, insert it into the points array at the correct index
        if (pendingHandle) {
          // If the pending handle is at the end of the segment, we need to insert it after the last point since we added the previous last point above
          const index = pendingHandle.index === points.length - 1 ? pendingHandle.index + 1 : pendingHandle.index;
          points.splice(index, 0, [pendingHandle.latitude, pendingHandle.longitude]);
        }

        const startIndex = 0;
        const startPoint = points[startIndex];
        const endIndex = points.length - 1;
        const endPoint = points[endIndex];
        const isStartEnd = points.length > 1 && isEqual(startPoint, endPoint);
        // The end point is in the same path as the point before it
        const endPathIndex = endIndex - 1;

        const start = point<MarkerProperties>([startPoint[1], startPoint[0]], {
          id: getMarkerId(segmentIndex, startIndex),
          segmentIndex,
          // Linking is an editing feature so only change the marker when a user is editing a route
          isLinkedToAdjacentSegment: isEditing && isLinkedToPreviousSegment(allSegments, segmentIndex),
          isStart: true,
          pathIndex: startIndex,
          latitude: startPoint[0],
          longitude: startPoint[1]
        });

        // Only set end properties on the start marker if it is a combined start/end marker
        if (isStartEnd) {
          start.properties.isEnd = true;
          start.properties.endPathIndex = endPathIndex;
          start.properties.isLinkedToAdjacentSegment =
            start.properties.isLinkedToAdjacentSegment || (isEditing && isLinkedToNextSegment(allSegments, segmentIndex));
        }

        // If there is only one point, we only need the start marker
        if (points.length === 1) {
          return [start];
        }

        const handles = points.slice(1, -1).map(([latitude, longitude], indexFromOne) => {
          const pathIndex = indexFromOne + 1; // Adjust index to match the points array including the start point

          // log analytics when a handle is added
          if (pendingHandle) {
            logCustomRouteUpdated({ custom_route_action_taken: 'add handle' });
          }

          return point<MarkerProperties>([longitude, latitude], {
            id: getMarkerId(segmentIndex, pathIndex),
            segmentIndex,
            pathIndex,
            latitude,
            longitude,
            position: showPositionTooltip ? getMarkerPosition(latitude, longitude, segment) : undefined
          });
        });

        // If the start and end points are the same, we don't need an end marker
        if (isStartEnd) {
          return [start, ...handles];
        }

        const end = point<MarkerProperties>([endPoint[1], endPoint[0]], {
          id: getMarkerId(segmentIndex, endIndex),
          segmentIndex,
          isEnd: true,
          isLinkedToAdjacentSegment: isEditing && isLinkedToNextSegment(allSegments, segmentIndex),
          pathIndex: endPathIndex,
          latitude: endPoint[0],
          longitude: endPoint[1]
        });

        return [start, ...handles, end];
      })
      .flat();

    return featureCollection<Point, MarkerProperties>(markers);
  }, [activeSegmentIndex, allSegments, isEditing, pendingHandle, showPositionTooltip]);

  // The drag layer draws the marker being dragged, so it is left out of the source rather than hidden with a layer
  // filter. Symbol filters are applied when the worker builds the tile, and the setData from this same render can
  // swallow the reload that setFilter needs, leaving the marker missing from the bucket until the next tile rebuild.
  // data itself is left intact because selectedMarker indexes into its features by position.
  const sourceData = useMemo(
    () =>
      draggedMarkerId ? featureCollection<Point, MarkerProperties>(data.features.filter(feature => feature.properties.id !== draggedMarkerId)) : data,
    [data, draggedMarkerId]
  );

  const selectedMarker = useMemo(() => {
    if (selectedHandleIndex === undefined || selectedHandleIndex > data.features.length) {
      return undefined;
    }

    // If the selected handle index is one more than the number of features it means a combined start/end marker is selected
    const markerIndex = selectedHandleIndex === data.features.length ? 0 : selectedHandleIndex;
    return data.features[markerIndex].properties as MarkerProperties;
  }, [data, selectedHandleIndex]);

  const onClose = useCallback(() => {
    setIsContextMenuOpen(false);
  }, [setIsContextMenuOpen]);

  // Close the context menu when state affecting the markers changes
  useEffect(() => {
    setIsContextMenuOpen(false);
  }, [activeSegmentIndex, allSegments, isEditingWaypoint, setIsContextMenuOpen]);

  // Register event handler for clicking on markers
  useEffect(() => {
    if (!isEditing) {
      return;
    }

    const onMarkerClick = (event: MapMouseEvent) => {
      if (!event.features) {
        return;
      }

      const { segmentIndex, isStart, isEnd, pathIndex, endPathIndex } = event.features[0].properties as MarkerProperties;
      // The endPathIndex will be defined if both isStart and isEnd are true
      const selectedPathIndex = isStart && isEnd ? endPathIndex! : pathIndex;
      // The handle index is one more than the path index for the end handle since it is at the end of the last path
      const newSelectedHandleIndex = isEnd ? selectedPathIndex + 1 : selectedPathIndex;

      const isHandleReselected = selectedHandleIndexRef.current === newSelectedHandleIndex;
      const isStartEndReselected = isStart && isEnd && (selectedHandleIndexRef.current === 0 || selectedHandleIndexRef.current === endPathIndex! + 1);

      if (isHandleReselected || isStartEndReselected) {
        if (!isContextMenuOpenRef.current) {
          setIsContextMenuOpen(true);
        }
        logCustomRouteContextualEditMenuViewed();
        return;
      }

      if (activeSegmentIndex !== segmentIndex) {
        dispatch(setActiveSegmentIndex({ activeSegmentIndex: segmentIndex, canEdit: true }));
      }

      dispatch(setSelectedHandleIndex(newSelectedHandleIndex));
    };

    registerEventHandler(customRouteMarkersId, 'click', onMarkerClick);

    return () => {
      unregisterEventHandler(customRouteMarkersId, 'click', onMarkerClick);
    };
  }, [
    isEditing,
    activeSegmentIndex,
    selectedHandleIndexRef,
    isContextMenuOpenRef,
    setIsContextMenuOpen,
    dispatch,
    registerEventHandler,
    unregisterEventHandler
  ]);

  return (
    <>
      <Source id={SOURCE_ID} type="geojson" data={sourceData}>
        <Layer id={customRouteMarkersId} source={SOURCE_ID} type="symbol" layout={layout} beforeId={beforeId} />
      </Source>
      <Source id={DRAG_SOURCE_ID} type="geojson" data={dragData}>
        <Layer id={customRouteMarkerDragId} source={DRAG_SOURCE_ID} type="symbol" layout={layout} beforeId={beforeId} />
      </Source>
      {isEditing && !isEditingWaypoint && !dragMarker && selectedMarker && (
        <PulsingMarker
          beforeId={beforeId}
          activeSegmentIndex={activeSegmentIndex}
          latitude={selectedMarker.latitude}
          longitude={selectedMarker.longitude}
        />
      )}
      {isContextMenuOpen && selectedMarker && <ContextMenu {...selectedMarker} onClose={onClose} />}
      {!isContextMenuOpen && hoveredMarker?.position && <PositionTooltip {...hoveredMarker} />}
    </>
  );
};

export default Markers;
